import React, { useEffect } from "react";
import { Mail, Shield } from "lucide-react";
import GlobalFooter from "../components/GlobalFooter";
import PublicLegalDocument from "../components/PublicLegalDocument";
import PrivacyPolicyAppendix from "../components/PrivacyPolicyAppendix";

export default function PrivacyPolicy() {
  useEffect(() => {
    // Hide Emergent copilot on public Privacy page
    const copilotWidget = document.querySelector('#emergent-copilot, [data-emergent-copilot], .emergent-chat-widget');
    if (copilotWidget) {
      copilotWidget.style.display = 'none';
    }
    
    return () => {
      if (copilotWidget) {
        copilotWidget.style.display = '';
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-[#0A0A0E] to-black text-white flex flex-col">
      <div className="container mx-auto px-6 py-12 max-w-4xl flex-1">
        {/* Header */}
        <div className="mb-12 border-b border-white/10 pb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/30">
              <Shield size={32} className="text-emerald-400" />
            </div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
              Privacy Policy
            </h1>
          </div>
          <p className="text-zinc-500">Last Updated: October 2026</p>
        </div>

        {/* Content */}
        <PublicLegalDocument docType="privacy" appendWhenSaved={<PrivacyPolicyAppendix />}>
        <div className="space-y-8 prose prose-invert max-w-none">
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">1. Information We Collect</h2>
            <p className="text-zinc-300 leading-relaxed">
              We collect information you provide directly to us, including when you create an account, 
              subscribe to content, or contact us for support. This may include your name, email address, 
              payment information, and any other information you choose to provide.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">2. How We Use Your Information</h2>
            <p className="text-zinc-300 leading-relaxed mb-4">We use the information we collect to:</p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4">
              <li>Provide, maintain, and improve our services</li>
              <li>Process payments and deliver purchased content</li>
              <li>Send you technical notices and support messages</li>
              <li>Respond to your comments and questions</li>
              <li>Protect against fraud and abuse</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">3. Information Sharing</h2>
            <p className="text-zinc-300 leading-relaxed">
              We do not sell your personal information. We may share your information with third-party 
              service providers who perform services on our behalf, such as payment processing, data 
              analysis, and customer support.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">4. Data Security</h2>
            <p className="text-zinc-300 leading-relaxed">
              We use industry-standard security measures to protect your personal information. However, 
              no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute 
              security.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">5. Your Rights (GDPR Compliance)</h2>
            <p className="text-zinc-300 leading-relaxed mb-4">
              Under the General Data Protection Regulation (GDPR), you have the right to:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4">
              <li>Access your personal data</li>
              <li>Rectify inaccurate personal data</li>
              <li>Request deletion of your personal data ("right to be forgotten")</li>
              <li>Object to processing of your personal data</li>
              <li>Request restriction of processing</li>
              <li>Data portability</li>
              <li>Withdraw consent at any time</li>
            </ul>
            <p className="text-zinc-300 leading-relaxed mt-4">
              To exercise any of these rights, please contact us using the information below.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">6. Cookies and Tracking</h2>
            <p className="text-zinc-300 leading-relaxed">
              We use cookies and similar tracking technologies to enhance your experience, analyze usage, 
              and assist in our marketing efforts. You can control cookies through your browser settings 
              or through our consent banner.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">7. Data Retention</h2>
            <p className="text-zinc-300 leading-relaxed">
              We retain your personal information for as long as necessary to provide our services and 
              fulfill the purposes outlined in this policy. When data is no longer needed, we securely 
              delete or anonymize it.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">8. International Data Transfers</h2>
            <p className="text-zinc-300 leading-relaxed">
              Your information may be transferred to and processed in countries other than your country 
              of residence. We ensure appropriate safeguards are in place to protect your data in 
              accordance with applicable laws.
            </p>
          </section>

          <section data-testid="privacy-instagram">
            <h2 className="text-2xl font-semibold text-white mb-4">9. Instagram Integration</h2>
            <p className="text-zinc-300 leading-relaxed">
              The site owner uses an internal dashboard (GP App) to schedule and publish posts to his own
              Instagram professional account through Meta&apos;s official Instagram API. For that account only, we
              store an encrypted access token, the Instagram account ID and username, and basic data about the
              owner&apos;s own posts (IDs, captions, timestamps, like/comment counts). We do not collect data about
              visitors or other Instagram users through this integration, and we do not sell or share it.
              The data is kept while the account is connected and is deleted within 30 days after the account
              is disconnected or a deletion request is received. To request deletion, use{" "}
              <a href="https://giopilos.com/deletion" className="text-emerald-400 hover:text-emerald-300">https://giopilos.com/deletion</a>{" "}
              or email{" "}
              <a href="mailto:privacy@giopilos.com" className="text-emerald-400 hover:text-emerald-300">privacy@giopilos.com</a>.
              You can also remove the app from Instagram under Settings &gt; Apps and websites.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">10. Contact Us</h2>
            <div className="backdrop-blur-xl bg-purple-900/20 border border-white/10 rounded-lg p-6">
              <p className="text-zinc-300 mb-4">
                If you have any questions about this Privacy Policy, wish to exercise your rights, 
                or have privacy concerns, please contact us:
              </p>
              <a 
                href="mailto:privacy@giopilos.com"
                className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 transition font-semibold"
              >
                <Mail size={18} />
                privacy@giopilos.com
              </a>
              <p className="text-zinc-500 text-sm mt-4">
                We will respond to all requests within 30 days as required by GDPR.
              </p>
            </div>
          </section>
        </div>
        </PublicLegalDocument>
      </div>
      <GlobalFooter />
    </div>
  );
}
